/**
 * Custom Address Lists Manager
 *
 * Allows administrators to create and manage custom IP/DNS address lists that can be
 * synchronized to all provisioned routers. Used for organization-specific address groups
 * beyond the standard Whitelist/Blacklist/L7 Rules.
 *
 * Features:
 * - Create new custom lists with IP addresses and DNS names
 * - Edit existing lists
 * - Delete lists
 * - View sync status across routers
 * - Manual sync to all routers
 * - Enable/disable auto-sync per list
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaPlus, FaEdit, FaTrash, FaSync, FaCheckCircle, FaTimesCircle,
    FaClock, FaExclamationTriangle, FaTimes, FaSave, FaChevronDown
} from 'react-icons/fa';
import './CustomAddressListsManager.scss';
import * as Sentry from '@sentry/react';

const CustomAddressListsManager = () => {
    const { apiFetch } = useAuth();
    const [loading, setLoading] = useState(true);
    const [lists, setLists] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingListId, setEditingListId] = useState(null);
    const [expandedListId, setExpandedListId] = useState(null);
    const [syncingListId, setSubscriptionListId] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        ipAddresses: '',
        dnsNames: '',
        syncToRouters: true
    });

    const loadCustomLists = useCallback(async () => {
        try {
            const response = await apiFetch('/api/RouterProvisioning/custom-address-lists');
            const data = await response.json();
            if (data?.success && data.data) {
                setLists(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to load custom address lists');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        loadCustomLists();
    }, [loadCustomLists]);

    const handleCreateList = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('List name is required');
            return;
        }

        try {
            const ipAddresses = formData.ipAddresses
                .split('\n')
                .map(ip => ip.trim())
                .filter(ip => ip);

            const dnsNames = formData.dnsNames
                .split('\n')
                .map(dns => dns.trim())
                .filter(dns => dns);

            const response = await apiFetch('/api/RouterProvisioning/custom-address-lists', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    description: formData.description.trim() || null,
                    ipAddresses,
                    dnsNames,
                    syncToRouters: formData.syncToRouters
                })
            });
            const data = await response.json();

            if (data?.success) {
                toast.success('Custom address list created successfully');
                setFormData({ name: '', description: '', ipAddresses: '', dnsNames: '', syncToRouters: true });
                setShowCreateForm(false);
                await loadCustomLists();
            } else {
                toast.error(data?.error || 'Failed to create custom address list');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error creating custom address list');
        }
    };

    const handleUpdateList = async (listId) => {
        try {
            const list = lists.find(l => l.id === listId);
            if (!list) return;

            const ipAddresses = formData.ipAddresses
                .split('\n')
                .map(ip => ip.trim())
                .filter(ip => ip);

            const dnsNames = formData.dnsNames
                .split('\n')
                .map(dns => dns.trim())
                .filter(dns => dns);

            const response = await apiFetch(`/api/RouterProvisioning/custom-address-lists/${listId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name || list.name,
                    description: formData.description,
                    ipAddresses,
                    dnsNames,
                    syncToRouters: formData.syncToRouters,
                    isActive: list.isActive
                })
            });
            const data = await response.json();

            if (data?.success) {
                toast.success('Custom address list updated successfully');
                setEditingListId(null);
                await loadCustomLists();
            } else {
                toast.error(data?.error || 'Failed to update custom address list');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error updating custom address list');
        }
    };

    const handleDeleteList = async (listId) => {
        if (!window.confirm('Are you sure you want to delete this custom address list?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/RouterProvisioning/custom-address-lists/${listId}`, {
                method: 'DELETE'
            });
            const data = await response.json();

            if (data?.success) {
                toast.success('Custom address list deleted successfully');
                await loadCustomLists();
            } else {
                toast.error(data?.error || 'Failed to delete custom address list');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error deleting custom address list');
        }
    };

    const handleSyncToAllRouters = async (listId) => {
        try {
            setSubscriptionListId(listId);
            const response = await apiFetch(`/api/RouterProvisioning/custom-address-lists/${listId}/sync-all`, {
                method: 'POST'
            });
            const data = await response.json();

            if (data?.success) {
                const { totalRouters, successCount, failedCount } = data.data;
                toast.success(`Synced to ${successCount}/${totalRouters} routers`);
                if (failedCount > 0) {
                    toast.warning(`${failedCount} router(s) failed to sync`);
                }
                await loadCustomLists();
            } else {
                toast.error(data?.error || 'Failed to sync custom address list');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error syncing custom address list');
        } finally {
            setSubscriptionListId(null);
        }
    };

    if (loading) {
        return (
            <div className="custom-lists-container">
                <div className="loading">Loading custom address lists...</div>
            </div>
        );
    }

    return (
        <div className="custom-lists-container">
            <div className="lists-header">
                <h3>Custom Address Lists</h3>
                <button
                    className="btn-create"
                    onClick={() => {
                        setShowCreateForm(!showCreateForm);
                        setEditingListId(null);
                        setFormData({ name: '', description: '', ipAddresses: '', dnsNames: '', syncToRouters: true });
                    }}
                >
                    <FaPlus /> New Custom List
                </button>
            </div>

            {/* Create/Edit Form */}
            {(showCreateForm || editingListId) && (
                <div className="form-panel">
                    <h4>{editingListId ? 'Edit Custom List' : 'Create New Custom List'}</h4>
                    <form onSubmit={editingListId ? (e) => {
                        e.preventDefault();
                        handleUpdateList(editingListId);
                    } : handleCreateList}>
                        <div className="form-group">
                            <label>List Name *</label>
                            <input
                                type="text"
                                placeholder="e.g., Partner_Networks, DNS_Blockers"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                maxLength="255"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                placeholder="Describe the purpose of this list..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                maxLength="1000"
                                rows="2"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>IP Addresses & CIDR Ranges (one per line)</label>
                                <textarea
                                    placeholder="203.0.113.0/24&#10;198.51.100.0/24"
                                    value={formData.ipAddresses}
                                    onChange={(e) => setFormData({ ...formData, ipAddresses: e.target.value })}
                                    rows="4"
                                />
                            </div>

                            <div className="form-group">
                                <label>DNS Names (one per line)</label>
                                <textarea
                                    placeholder="partner1.example.com&#10;partner2.example.com"
                                    value={formData.dnsNames}
                                    onChange={(e) => setFormData({ ...formData, dnsNames: e.target.value })}
                                    rows="4"
                                />
                            </div>
                        </div>

                        <div className="form-group checkbox">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.syncToRouters}
                                    onChange={(e) => setFormData({ ...formData, syncToRouters: e.target.checked })}
                                />
                                Automatically sync to all provisioned routers
                            </label>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn-save">
                                <FaSave /> {editingListId ? 'Update List' : 'Create List'}
                            </button>
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setEditingListId(null);
                                    setFormData({ name: '', description: '', ipAddresses: '', dnsNames: '', syncToRouters: true });
                                }}
                            >
                                <FaTimes /> Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Lists Table */}
            <div className="lists-table">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Entries</th>
                            <th>Auto-Sync</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lists.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="empty-state">
                                    No custom address lists created yet. Create one to get started!
                                </td>
                            </tr>
                        ) : (
                            lists.map((list) => (
                                <tr key={list.id} className={expandedListId === list.id ? 'expanded' : ''}>
                                    <td className="name">
                                        <button
                                            className="expand-btn"
                                            onClick={() => setExpandedListId(expandedListId === list.id ? null : list.id)}
                                        >
                                            <FaChevronDown /> {list.name}
                                        </button>
                                    </td>
                                    <td className="description">{list.description || '—'}</td>
                                    <td className="entries">
                                        {(list.ipAddresses?.length || 0) + (list.dnsNames?.length || 0)}
                                    </td>
                                    <td className="auto-sync">
                                        {list.syncToRouters ? 'Yes' : 'No'}
                                    </td>
                                    <td className="status">
                                        {list.isActive ? (
                                            <span className="badge active">Active</span>
                                        ) : (
                                            <span className="badge inactive">Inactive</span>
                                        )}
                                    </td>
                                    <td className="actions">
                                        <button
                                            className="btn-action sync"
                                            onClick={() => handleSyncToAllRouters(list.id)}
                                            disabled={syncingListId === list.id}
                                            title="Manually sync this list to all routers"
                                        >
                                            <FaSync /> {syncingListId === list.id ? 'Syncing...' : 'Sync'}
                                        </button>
                                        <button
                                            className="btn-action edit"
                                            onClick={() => {
                                                setEditingListId(list.id);
                                                setShowCreateForm(false);
                                                setFormData({
                                                    name: list.name,
                                                    description: list.description || '',
                                                    ipAddresses: (list.ipAddresses || []).join('\n'),
                                                    dnsNames: (list.dnsNames || []).join('\n'),
                                                    syncToRouters: list.syncToRouters
                                                });
                                            }}
                                            title="Edit this list"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-action delete"
                                            onClick={() => handleDeleteList(list.id)}
                                            title="Delete this list"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomAddressListsManager;
